import { useCallback } from 'react';
import { sum } from '@shared/helpers/math.helpers';
import {
  EnergyClassInfo,
  getEnergyByCarrierByConsumer,
  getPrimaryEnergyValues,
  getMaxAllowedPrimaryEnergyValue,
} from '@shared/helpers/energy/energy-class';
import {
  useGetYearlyEnergyConsumptionRecord,
  useHeatedArea,
} from '@features/energy/energy.hook';
import { useActivityMix } from '@features/design/hooks/activities.hooks';
import {
  energyCarrierNames,
  energyConsumerNames,
  EnergyCarrier,
  EnergyConsumer,
} from '@shared/helpers/energy/energy-class.model';
import {
  ConsumptionItem,
  GenerationItem,
} from '../ReportOperationalEnergy.model';
import { StackedBarGraphData } from '../components/HorizontalStackedBarChart';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { IProposal } from '@shared/models/proposals.model';
import { useBuildingLocation } from '@features/design/hooks/design.hooks';
import { getGeographicalAdjustmentFactor } from '@shared/helpers/energy/city-climate.helpers';

type StackedBarSegment = StackedBarGraphData['segments'][number];

export type OperationalEnergyItem = {
  label: string;
  energyValue: number;
  carbonValue: number;
  graphData: {
    totalWidthPercent: number;
    segments: StackedBarSegment[];
  };
};

export type EnergyClassData = {
  energyClassInfo: EnergyClassInfo;
  primaryEnergyScore: number;
};

export type OperationalEnergyData = {
  operationalEnergyData: OperationalEnergyItem[];
  consumptionData: ConsumptionItem[];
  generationData: GenerationItem[];
  energyClassData: EnergyClassData;
};

export const useGetOperationalEnergyByProposal = (): ((
  proposal: ItemOrItemId<IProposal | IProposal>,
) => OperationalEnergyData) => {
  const getYearlyEnergyConsumptionRecord =
    useGetYearlyEnergyConsumptionRecord();

  const heatedArea = useHeatedArea();
  const activityMix = useActivityMix();

  const location = useBuildingLocation();
  const geoFactor = getGeographicalAdjustmentFactor(location);

  return useCallback(
    (proposal: ItemOrItemId<IProposal | IProposal>) => {
      const energyConsumptionRecord =
        getYearlyEnergyConsumptionRecord(proposal);
      const co2eConsumptionRecord = getYearlyEnergyConsumptionRecord(
        proposal,
        'co2e_B6',
      );
      const energyByCarrier = getEnergyByCarrierByConsumer(
        energyConsumptionRecord,
      );
      const co2eByCarrier = getEnergyByCarrierByConsumer(co2eConsumptionRecord);

      // Find the largest CO2e value for scaling (largest gets 100%)
      const maxCO2eValue = Math.max(
        ...Object.values(co2eByCarrier).map((record) => sum(record)),
      );

      // Group energy consumption by carrier (district heating, electricity, etc.)
      const operationalEnergyData =
        Object.keys(energyByCarrier).length === 0
          ? [
              {
                label: 'No operational energy data',
                energyValue: 0,
                graphData: {
                  totalWidthPercent: 0,
                  segments: [],
                },
                carbonValue: 0,
              },
            ]
          : Object.entries(energyByCarrier).map(
              ([carrier, consumers]): OperationalEnergyItem => {
                const energyCarrier = carrier as EnergyCarrier;
                const carrierTotalEnergy = sum(consumers);

                const segments = Object.entries(consumers).map(
                  ([consumer, value]): StackedBarSegment => {
                    const energyConsumer = consumer as EnergyConsumer;
                    return {
                      id: energyConsumer,
                      label: energyConsumerNames[energyConsumer],
                      value,
                      sizePercent: (value / carrierTotalEnergy) * 100,
                    };
                  },
                );

                // Calculate CO2e value by summing up the CO2e values for each consumer
                const co2eValue = sum(co2eByCarrier[energyCarrier]);

                // Calculate bar width as percentage of the largest CO2e value (largest gets 100%)
                const barWidthPercentage =
                  maxCO2eValue === 0 ? 0 : (co2eValue / maxCO2eValue) * 100;

                return {
                  label: energyCarrierNames[energyCarrier],
                  energyValue: carrierTotalEnergy,
                  graphData: {
                    totalWidthPercent: barWidthPercentage,
                    segments,
                  },
                  carbonValue: co2eValue,
                };
              },
            );

      const consumptionData = [
        EnergyConsumer.Heating,
        EnergyConsumer.Cooling,
        EnergyConsumer.Tapwater,
        EnergyConsumer.Building,
      ].map((consumer) => ({
        consumer: consumer,
        label: energyConsumerNames[consumer],
        value: sum(energyConsumptionRecord[consumer]),
      }));

      const totalPrimaryEnergy = sum(
        getPrimaryEnergyValues(energyConsumptionRecord, heatedArea, geoFactor),
      );

      const maxAllowedPrimaryEnergyValue = getMaxAllowedPrimaryEnergyValue(
        heatedArea,
        activityMix,
      );

      const energyClassData: EnergyClassData = {
        energyClassInfo: EnergyClassInfo.fromPrimaryEnergyValue(
          totalPrimaryEnergy,
          maxAllowedPrimaryEnergyValue,
        ),
        primaryEnergyScore: totalPrimaryEnergy,
      };

      // This would need to be connected to actual generation data from the project
      // For now, returning empty array as placeholder
      const generationData: GenerationItem[] = [];

      return {
        generationData,
        operationalEnergyData,
        consumptionData,
        energyClassData,
      };
    },
    [activityMix, geoFactor, getYearlyEnergyConsumptionRecord, heatedArea],
  );
};
